import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';
import { getPortfolioReturnSessionId } from '../utils/portfolioReturnSession';
import { canonicalizeCountry, createHierarchy } from '../utils/filterHierarchy';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const PORTFOLIO_RETURN_STATE_KEY = 'portfolioReturn';

async function readApiJson(response, requestLabel) {
  const contentType = response.headers?.get?.('content-type') || '';
  if (contentType && !contentType.toLowerCase().includes('application/json')) {
    const body = typeof response.text === 'function' ? await response.text() : '';
    const isHtml = /^\s*<!doctype html|^\s*<html/i.test(body);
    throw new Error(
      `${requestLabel} returned an unexpected ${isHtml ? 'HTML' : 'non-JSON'} response (HTTP ${response.status}).`
    );
  }
  try {
    return await response.json();
  } catch (_) {
    throw new Error(`${requestLabel} returned an invalid JSON response (HTTP ${response.status}).`);
  }
}

async function readApiError(response, requestLabel) {
  const payload = await readApiJson(response, requestLabel);
  if (typeof payload?.detail === 'string') return payload.detail;
  if (payload?.detail !== undefined) return JSON.stringify(payload.detail);
  return `${requestLabel} failed (HTTP ${response.status}).`;
}

const PREMIUM_SETTINGS_FIELDS = [
  'country', 'universe_code', 'sector', 'industry', 'top_n', 'investor_score_tolerance',
];

function premiumSettingsMatch(current, saved) {
  return Boolean(current && saved) && PREMIUM_SETTINGS_FIELDS.every(
    (field) => (current[field] ?? null) === (saved[field] ?? null)
  );
}

function savedSettingValue(value, allLabel) {
  return value === null || value === undefined || value === '' ? allLabel : value;
}

const RANKING_UNIVERSE_OPTIONS = [
  { value: 'ALL', label: 'All indexed tickers (United States + Canada)' },
  { value: 'SP500', label: 'S&P 500' },
  { value: 'NASDAQ100', label: 'Nasdaq-100' },
  { value: 'DOW30', label: 'Dow 30' },
  { value: 'TSX60', label: 'S&P/TSX 60' },
  { value: 'TSX_COMPOSITE', label: 'S&P/TSX Composite' },
];

const getRankingUniverseLabel = (code) => {
  const option = RANKING_UNIVERSE_OPTIONS.find((item) => item.value === code);
  return option ? option.label : 'Unknown universe';
};

const hasDisplayValue = (value) => value !== null && value !== undefined;

function PortfolioOpportunityTable({
  stocks,
  onAddOpportunityToPortfolio,
  onTickerNavigate = () => {},
  owned = false,
}) {
  return (
    <div className="portfolio-table-scroll">
      <table className="theme-table portfolio-table">
      <thead>
        <tr>
          <th>Ticker</th>
          <th>Global Rank</th>
          <th>Sector</th>
          <th>Industry</th>
          <th>Investment Score</th>
          <th>Entry Timing</th>
          <th>Price</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {stocks.map((stock, index) => (
          <tr key={`${stock.ticker}-${index}`}>
            <td>
              <Link
                to={`/stock/${stock.ticker}`}
                title="View stock research page"
                className="portfolio-ticker-link"
                onClick={(event) => onTickerNavigate(event, stock.ticker)}
              >
                {stock.ticker}
              </Link>
            </td>
            <td>{hasDisplayValue(stock.rank) ? stock.rank : 'N/A'}</td>
            <td>{stock.sector || 'N/A'}</td>
            <td>{stock.industry || 'N/A'}</td>
            <td className="portfolio-table-score-cell">
              {hasDisplayValue(stock.investment_score) ? stock.investment_score.toFixed(1) : 'N/A'}
            </td>
            <td>{stock.entry_timing || 'N/A'}</td>
            <td>{hasDisplayValue(stock.price) ? `$${stock.price.toFixed(2)}` : 'N/A'}</td>
            <td>
              {owned ? (
                <span className="theme-text-muted">Already held</span>
              ) : (
                <button
                  onClick={() => onAddOpportunityToPortfolio(stock.ticker)}
                  className="portfolio-btn portfolio-btn-primary portfolio-btn-compact"
                >
                  Add to Portfolio
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
      </table>
    </div>
  );
}

function Portfolios({
  fetchNotifications = async () => {},
  userInfo = null,
  loadingUserInfo = false,
  portfolioManagerRefreshKey = 0
}) {
  const [portfolios, setPortfolios] = useState([]);
  const [loadedUserInfo, setLoadedUserInfo] = useState(null);
  const [loadingLoadedUserInfo, setLoadingLoadedUserInfo] = useState(!userInfo);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [portfolioDetail, setPortfolioDetail] = useState(null);
  const [showAddHolding, setShowAddHolding] = useState(false);
  const [newHolding, setNewHolding] = useState({
    ticker: '',
    quantity: '',
    average_buy_price: '',
    purchase_date: ''
  });
  const [comparisonData, setComparisonData] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [latestScanId, setLatestScanId] = useState(null);
  const [premiumAlertSettings, setPremiumAlertSettings] = useState(null);
  const [savedPremiumAlertSettings, setSavedPremiumAlertSettings] = useState(null);
  const [premiumAlertSettingsError, setPremiumAlertSettingsError] = useState('');
  const [savingPremiumAlertSettings, setSavingPremiumAlertSettings] = useState(false);
  const [premiumStrategyFilterOptions, setPremiumStrategyFilterOptions] = useState({ indexCode: null, records: [] });
  const [premiumStrategyFilterOptionsError, setPremiumStrategyFilterOptionsError] = useState('');
  const [showAddOpportunityModal, setShowAddOpportunityModal] = useState(false);
  const [opportunityToAdd, setOpportunityToAdd] = useState(null);
  const [opportunityHoldingData, setOpportunityHoldingData] = useState({
    quantity: '',
    average_buy_price: ''
  });
  const [showEditHoldingModal, setShowEditHoldingModal] = useState(false);
  const [editingHolding, setEditingHolding] = useState(null);
  const [editHoldingData, setEditHoldingData] = useState({
    quantity: '',
    average_buy_price: ''
  });
  const [rebalancing, setRebalancing] = useState(false);
  const [rebalanceSuccess, setRebalanceSuccess] = useState('');
  const [allowFractionalShares, setAllowFractionalShares] = useState(true);
  const [lastRebalanceSettings, setLastRebalanceSettings] = useState(null);
  const selectedPortfolioRef = React.useRef(null);
  const activePortfolioRequestRef = React.useRef(0);

  const rememberExpandedPortfolio = React.useCallback((portfolioId, event) => {
    if (
      portfolioId === null
      || portfolioId === undefined
      || event.button !== 0
      || event.metaKey
      || event.altKey
      || event.ctrlKey
      || event.shiftKey
    ) {
      return;
    }

    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: {
        ...location.state,
        [PORTFOLIO_RETURN_STATE_KEY]: {
          portfolioId,
          sessionId: getPortfolioReturnSessionId(),
        },
      },
    });
  }, [location, navigate]);

  useEffect(() => {
    selectedPortfolioRef.current = selectedPortfolio;
  }, [selectedPortfolio]);

  const clearSelectedPortfolioState = React.useCallback(({ clearSelection = true } = {}) => {
    activePortfolioRequestRef.current += 1;

    if (clearSelection) {
      setSelectedPortfolio(null);
    }

    setPortfolioDetail(null);
    setPremiumAlertSettings(null);
    setSavedPremiumAlertSettings(null);
    setPremiumAlertSettingsError('');
    setComparisonData(null);
    setLatestScanId(null);
    setShowAddHolding(false);
    setShowAddOpportunityModal(false);
    setOpportunityToAdd(null);
    setShowEditHoldingModal(false);
    setEditingHolding(null);
    setLoadingComparison(false);
    setRebalanceSuccess('');
    setLastRebalanceSettings(null);
  }, []);

  const isMissingPortfolioResponse = (statusCode, detail) =>
    statusCode === 404 || detail === 'Portfolio not found or access denied';

  const handleExportPDF = async (scanId, portfolioId = null) => {
    setError('');

    try {
      let url = `${API_BASE_URL}/scans/${scanId}/export-pdf`;
      
      // Add portfolio_id query parameter if provided
      if (portfolioId) {
        url += `?portfolio_id=${portfolioId}`;
      }
      
      const response = await apiFetch(url, {
        method: 'GET',
        headers: {
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      // Create blob from response
      const blob = await response.blob();
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      
      if (portfolioId) {
        a.download = `investor_report_scan_${scanId}_portfolio_${portfolioId}_${new Date().toISOString().split('T')[0]}.pdf`;
      } else {
        a.download = `investor_report_scan_${scanId}_${new Date().toISOString().split('T')[0]}.pdf`;
      }
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (err) {
      setError(`Failed to export PDF: ${err.message}`);
    }
  };

  const handleRebalancePortfolio = async (mode) => {
    if (!selectedPortfolio) {
      setError('No portfolio selected');
      return;
    }

    // Check if this is a repeated click with same settings
    if (lastRebalanceSettings && 
        lastRebalanceSettings.mode === mode && 
        lastRebalanceSettings.allowFractionalShares === allowFractionalShares &&
        lastRebalanceSettings.portfolioId === selectedPortfolio) {
      setRebalanceSuccess(`This portfolio is already rebalanced using ${mode} mode with fractional shares ${allowFractionalShares ? 'enabled' : 'disabled'}.`);
      setTimeout(() => {
        setRebalanceSuccess('');
      }, 5000);
      return;
    }

    // Confirm with user
    const fractionalSharesText = allowFractionalShares ? 'enabled' : 'disabled';
    const confirmed = window.confirm(
      `This will recalculate portfolio quantities using the current portfolio value and ${mode} weighting. Average buy prices will not change. Fractional shares: ${fractionalSharesText}. Continue?`
    );

    if (!confirmed) {
      return;
    }

    setRebalancing(true);
    setError('');
    setRebalanceSuccess('');

    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio/${selectedPortfolio}/rebalance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          mode: mode,
          allow_fractional_shares: allowFractionalShares
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const data = await response.json();
      
      let successMessage = `Portfolio rebalanced successfully using ${mode} mode. ${data.holdings_updated} holdings updated.`;
      
      // Show unallocated cash for whole-share mode
      if (!allowFractionalShares && data.estimated_unallocated_cash !== null && data.estimated_unallocated_cash !== undefined) {
        successMessage += ` Estimated unallocated cash: $${data.estimated_unallocated_cash.toFixed(2)}.`;
      }
      
      setRebalanceSuccess(successMessage);
      
      // Remember successful rebalance settings
      setLastRebalanceSettings({
        mode: mode,
        allowFractionalShares: allowFractionalShares,
        portfolioId: selectedPortfolio
      });
      
      // Refresh portfolio detail and list
      await handleViewPortfolio(selectedPortfolio);
      await fetchPortfolios();
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setRebalanceSuccess('');
      }, 5000);
      
    } catch (err) {
      setError(`Failed to rebalance portfolio: ${err.message}`);
    } finally {
      setRebalancing(false);
    }
  };

  const fetchPortfolios = useCallback(async () => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio`, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const data = await response.json();
      setPortfolios(data.portfolios || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const effectiveUserInfo = userInfo || loadedUserInfo;
  const isLoadingEffectiveUserInfo = Boolean(loadingUserInfo || (!userInfo && loadingLoadedUserInfo));
  const isPremium = effectiveUserInfo?.plan_type === 'unlimited';

  useEffect(() => {
    let cancelled = false;
    if (userInfo) {
      setLoadingLoadedUserInfo(false);
      return () => { cancelled = true; };
    }
    setLoadingLoadedUserInfo(true);
    apiFetch(`${API_BASE_URL}/me`, { method: 'GET' })
      .then(async (response) => {
        if (!response.ok) throw new Error('Unable to load account plan.');
        return response.json();
      })
      .then((data) => {
        if (!cancelled) setLoadedUserInfo(data);
      })
      .catch(() => {
        // Protected routing already handles authentication. Keep the existing
        // non-Premium interface if account metadata cannot be read.
        if (!cancelled) setLoadedUserInfo(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingLoadedUserInfo(false);
      });
    return () => { cancelled = true; };
  }, [userInfo]);

  const fetchPremiumStrategyFilterOptions = useCallback(async (universeCode, requestId = activePortfolioRequestRef.current) => {
    const requestedUniverse = universeCode || 'ALL';
    try {
      const response = await apiFetch(`${API_BASE_URL}/validation-lab/filter-options?index_code=${encodeURIComponent(requestedUniverse)}`, { method: 'GET' });
      if (!response.ok) throw new Error(await readApiError(response, 'Strategy Lab filter choices request'));
      const data = await readApiJson(response, 'Strategy Lab filter choices request');
      if (data.index_code !== requestedUniverse) {
        throw new Error('Strategy Lab filter choices returned a different universe.');
      }
      if (requestId === activePortfolioRequestRef.current) {
        setPremiumStrategyFilterOptions({
          indexCode: data.index_code,
          records: Array.isArray(data.records) ? data.records : [],
        });
        setPremiumStrategyFilterOptionsError('');
      }
    } catch (err) {
      if (requestId === activePortfolioRequestRef.current) {
        setPremiumStrategyFilterOptions({ indexCode: null, records: [] });
        setPremiumStrategyFilterOptionsError(err.message);
      }
    }
  }, []);

  const fetchPremiumAlertSettings = useCallback(async (portfolioId, requestId = activePortfolioRequestRef.current) => {
    if (!isPremium) {
      setPremiumAlertSettings(null);
      return;
    }
    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio/${portfolioId}/premium-alert-settings`, { method: 'GET' });
      if (!response.ok) throw new Error(await readApiError(response, 'Premium alert settings load request'));
      const data = await readApiJson(response, 'Premium alert settings load request');
      if (requestId === activePortfolioRequestRef.current) {
        const settings = { ...data, country: canonicalizeCountry(data.country) || null };
        setPremiumAlertSettings(settings);
        setSavedPremiumAlertSettings(settings);
        setPremiumAlertSettingsError('');
        fetchPremiumStrategyFilterOptions(settings.universe_code, requestId);
      }
    } catch (err) {
      if (requestId === activePortfolioRequestRef.current) setPremiumAlertSettingsError(err.message);
    }
  }, [isPremium, fetchPremiumStrategyFilterOptions]);

  useEffect(() => {
    if (isPremium && selectedPortfolio) {
      fetchPremiumAlertSettings(selectedPortfolio);
    }
  }, [isPremium, selectedPortfolio, fetchPremiumAlertSettings]);

  const savePremiumAlertSettings = async () => {
    if (!selectedPortfolio || !premiumAlertSettings) return;
    setSavingPremiumAlertSettings(true);
    setPremiumAlertSettingsError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio/${selectedPortfolio}/premium-alert-settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(premiumAlertSettings),
      });
      if (!response.ok) throw new Error(await readApiError(response, 'Premium alert settings save request'));
      const saved = await readApiJson(response, 'Premium alert settings save request');
      const normalizedSaved = { ...saved, country: canonicalizeCountry(saved.country) || null };
      setPremiumAlertSettings(normalizedSaved);
      setSavedPremiumAlertSettings(normalizedSaved);
      setPremiumAlertSettingsError('');
    } catch (err) {
      setPremiumAlertSettingsError(err.message);
    } finally {
      setSavingPremiumAlertSettings(false);
    }
  };

  const premiumAlertSettingsDirty = Boolean(
    premiumAlertSettings
    && savedPremiumAlertSettings
    && !premiumSettingsMatch(premiumAlertSettings, savedPremiumAlertSettings)
  );
  const selectedPortfolioName = portfolioDetail?.name
    || portfolios.find((portfolio) => String(portfolio.id) === String(selectedPortfolio))?.name
    || 'this portfolio';

  const premiumStrategyHierarchy = useMemo(
    () => createHierarchy(premiumStrategyFilterOptions.records),
    [premiumStrategyFilterOptions.records]
  );
  const premiumStrategyHierarchyReady = (
    premiumStrategyFilterOptions.indexCode === premiumAlertSettings?.universe_code
  );
  const premiumCountryOptions = useMemo(
    () => premiumStrategyHierarchyReady
      ? premiumStrategyHierarchy.getCountries(premiumAlertSettings.universe_code)
      : [],
    [premiumStrategyHierarchy, premiumStrategyHierarchyReady, premiumAlertSettings?.universe_code]
  );
  const premiumSectorOptions = useMemo(
    () => premiumStrategyHierarchyReady
      ? premiumStrategyHierarchy.getSectors(
        premiumAlertSettings.universe_code,
        premiumAlertSettings.country || null
      )
      : [],
    [premiumStrategyHierarchy, premiumStrategyHierarchyReady, premiumAlertSettings?.universe_code, premiumAlertSettings?.country]
  );
  const premiumIndustryOptions = useMemo(
    () => premiumStrategyHierarchyReady
      ? premiumStrategyHierarchy.getIndustries(
        premiumAlertSettings.universe_code,
        premiumAlertSettings.country || null,
        premiumAlertSettings.sector || null
      )
      : [],
    [premiumStrategyHierarchy, premiumStrategyHierarchyReady, premiumAlertSettings?.universe_code, premiumAlertSettings?.country, premiumAlertSettings?.sector]
  );

  const handleCreatePortfolio = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newPortfolioName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      setNewPortfolioName('');
      fetchPortfolios();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleViewPortfolio = useCallback(async (portfolioId) => {
    const requestId = activePortfolioRequestRef.current + 1;
    activePortfolioRequestRef.current = requestId;

    setSelectedPortfolio(portfolioId);
    setPortfolioDetail(null);
    setComparisonData(null);
    setLatestScanId(null);
    setError('');
    
    // Reset rebalance settings when switching portfolios
    if (portfolioId !== selectedPortfolioRef.current) {
      setLastRebalanceSettings(null);
    }

    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio/${portfolioId}`, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json();
        const detail =
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail);

        if (requestId === activePortfolioRequestRef.current && isMissingPortfolioResponse(response.status, detail)) {
          clearSelectedPortfolioState();
          setError('');
          return;
        }

        throw new Error(detail);
      }

      const data = await response.json();

      if (requestId !== activePortfolioRequestRef.current) {
        return;
      }

      setPortfolioDetail(data);
      
      // Premium settings are portfolio-owned; Free and Pro retain shared preferences.
      await fetchPremiumAlertSettings(portfolioId, requestId);
    } catch (err) {
      if (requestId !== activePortfolioRequestRef.current) {
        return;
      }

      setError(err.message);
    }
  }, [clearSelectedPortfolioState, fetchPremiumAlertSettings]);

  const handleViewPortfolioRef = React.useRef(handleViewPortfolio);

  useEffect(() => {
    handleViewPortfolioRef.current = handleViewPortfolio;
  }, [handleViewPortfolio]);

  useEffect(() => {
    const portfolioReturn = location.state?.[PORTFOLIO_RETURN_STATE_KEY];
    if (loading || !portfolioReturn) {
      return;
    }

    const isCurrentSession = portfolioReturn.sessionId === getPortfolioReturnSessionId();
    const restoredPortfolio = isCurrentSession
      ? portfolios.find(
        (portfolio) => String(portfolio.id) === String(portfolioReturn.portfolioId)
      )
      : null;

    const nextState = { ...location.state };
    delete nextState[PORTFOLIO_RETURN_STATE_KEY];
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: Object.keys(nextState).length > 0 ? nextState : null,
    });

    if (restoredPortfolio && handleViewPortfolioRef.current) {
      handleViewPortfolioRef.current(restoredPortfolio.id);
    }
  }, [loading, location, navigate, portfolios]);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  // Refresh portfolios when Dashboard triggers a refresh (e.g., after adding from saved scan)
  useEffect(() => {
    if (portfolioManagerRefreshKey > 0) {
      fetchPortfolios();
      const activePortfolioId = selectedPortfolioRef.current;
      if (activePortfolioId && handleViewPortfolioRef.current) {
        handleViewPortfolioRef.current(activePortfolioId);
      }
    }
  }, [portfolioManagerRefreshKey, fetchPortfolios]);

  const handleAddOpportunityToPortfolio = (ticker) => {
    // Open modal to collect quantity and price
    setOpportunityToAdd(ticker);
    setOpportunityHoldingData({
      quantity: '',
      average_buy_price: ''
    });
    setShowAddOpportunityModal(true);
  };

  const handleConfirmAddOpportunity = async () => {
    setError('');

    if (portfolios.length === 0) {
      return;
    }

    // Validate inputs
    if (!opportunityHoldingData.quantity || !opportunityHoldingData.average_buy_price) {
      setError('Please enter both Quantity and Average Buy Price');
      return;
    }
    
    const quantity = parseFloat(opportunityHoldingData.quantity);
    const avgPrice = parseFloat(opportunityHoldingData.average_buy_price);
    
    if (isNaN(quantity) || quantity <= 0) {
      setError('Quantity must be a positive number');
      return;
    }
    
    if (isNaN(avgPrice) || avgPrice <= 0) {
      setError('Average Buy Price must be a positive number');
      return;
    }
    
    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          portfolio_id: selectedPortfolio,
          ticker: opportunityToAdd,
          quantity: quantity,
          average_buy_price: avgPrice
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      // Close modal and reset state
      setShowAddOpportunityModal(false);
      setOpportunityToAdd(null);
      setOpportunityHoldingData({
        quantity: '',
        average_buy_price: ''
      });
      
      // Reset rebalance settings when holdings change
      setLastRebalanceSettings(null);
      
      // Refresh portfolio detail, list, and alerts
      await handleViewPortfolio(selectedPortfolio);
      await fetchPortfolios();
    } catch (err) {
      setError(`Failed to add ${opportunityToAdd}: ${err.message}`);
    }
  };

  const handleCancelAddOpportunity = () => {
    setShowAddOpportunityModal(false);
    setOpportunityToAdd(null);
    setOpportunityHoldingData({
      quantity: '',
      average_buy_price: ''
    });
    setError('');
  };

  const handleEditHolding = (holding) => {
    setEditingHolding(holding);
    setEditHoldingData({
      quantity: holding.quantity !== null ? holding.quantity.toString() : '',
      average_buy_price: holding.average_buy_price !== null ? holding.average_buy_price.toString() : ''
    });
    setShowEditHoldingModal(true);
  };

  const handleConfirmEditHolding = async () => {
    setError('');
    
    // Validate inputs
    if (!editHoldingData.quantity || !editHoldingData.average_buy_price) {
      setError('Please enter both Quantity and Average Buy Price');
      return;
    }
    
    const quantity = parseFloat(editHoldingData.quantity);
    const avgPrice = parseFloat(editHoldingData.average_buy_price);
    
    if (isNaN(quantity) || quantity <= 0) {
      setError('Quantity must be a positive number');
      return;
    }
    
    if (isNaN(avgPrice) || avgPrice <= 0) {
      setError('Average Buy Price must be a positive number');
      return;
    }
    
    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          portfolio_id: selectedPortfolio,
          ticker: editingHolding.ticker,
          quantity: quantity,
          average_buy_price: avgPrice
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      // Close modal and reset state
      setShowEditHoldingModal(false);
      setEditingHolding(null);
      setEditHoldingData({
        quantity: '',
        average_buy_price: ''
      });
      
      // Reset rebalance settings when holdings change
      setLastRebalanceSettings(null);
      
      // Refresh portfolio detail
      await handleViewPortfolio(selectedPortfolio);
    } catch (err) {
      setError(`Failed to update ${editingHolding.ticker}: ${err.message}`);
    }
  };

  const handleCancelEditHolding = () => {
    setShowEditHoldingModal(false);
    setEditingHolding(null);
    setEditHoldingData({
      quantity: '',
      average_buy_price: ''
    });
    setError('');
  };

  const handleRemoveHolding = async (ticker) => {
    if (!window.confirm(`Are you sure you want to remove ${ticker} from your portfolio?`)) {
      return;
    }
    
    setError('');
    
    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          portfolio_id: selectedPortfolio,
          ticker: ticker
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      // Reset rebalance settings when holdings change
      setLastRebalanceSettings(null);
      
      // Refresh portfolio detail and list
      await handleViewPortfolio(selectedPortfolio);
      await fetchPortfolios();
    } catch (err) {
      setError(`Failed to remove ${ticker}: ${err.message}`);
    }
  };

  const handleRemoveHoldingFromPortfolio = async (ticker) => {
    if (!window.confirm(`Are you sure you want to remove ${ticker} from your portfolio?`)) {
      return;
    }
    
    setError('');
    
    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          portfolio_id: selectedPortfolio,
          ticker: ticker
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      // Reset rebalance settings when holdings change
      setLastRebalanceSettings(null);
      
      // Refresh portfolio detail, list, and alerts
      await handleViewPortfolio(selectedPortfolio);
      await fetchPortfolios();
    } catch (err) {
      setError(`Failed to remove ${ticker}: ${err.message}`);
    }
  };

  const handleAddHolding = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const requestBody = {
        portfolio_id: selectedPortfolio,
        ticker: newHolding.ticker.toUpperCase(),
      };

      if (newHolding.quantity) {
        requestBody.quantity = parseFloat(newHolding.quantity);
      }
      if (newHolding.average_buy_price) {
        requestBody.average_buy_price = parseFloat(newHolding.average_buy_price);
      }
      if (newHolding.purchase_date) {
        requestBody.purchase_date = newHolding.purchase_date;
      }

      const response = await apiFetch(`${API_BASE_URL}/portfolio/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      setNewHolding({ ticker: '', quantity: '', average_buy_price: '', purchase_date: '' });
      setShowAddHolding(false);
      
      // Reset rebalance settings when holdings change
      setLastRebalanceSettings(null);
      
      await handleViewPortfolio(selectedPortfolio);
      await fetchPortfolios();
    
      if (fetchNotifications) {
          await fetchNotifications();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeletePortfolio = async (portfolioId) => {
    if (!window.confirm('Are you sure you want to delete this portfolio?')) {
      return;
    }

    setError('');

    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio/${portfolioId}`, {
        method: 'DELETE',
        headers: {
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const deletedSelectedPortfolio =
        String(selectedPortfolioRef.current) === String(portfolioId);

      setPortfolios((currentPortfolios) =>
        currentPortfolios.filter(
          (portfolio) => String(portfolio.id) !== String(portfolioId)
        )
      );
      setError('');

      if (deletedSelectedPortfolio) {
        clearSelectedPortfolioState();
      }

      await fetchPortfolios();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCompareWithLatestScan = async (portfolioId) => {
    setLoadingComparison(true);
    setComparisonData(null);
    setLatestScanId(null);
    setError('');

    try {
      
      // First, get the latest scan
      const scansResponse = await apiFetch(`${API_BASE_URL}/scans`, { method: 'GET' });

      if (!scansResponse.ok) {
        const errorData = await scansResponse.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const scansData = await scansResponse.json();
      
      if (!scansData.scans || scansData.scans.length === 0) {
        setError('No scans available for comparison. Please run a scan first.');
        setLoadingComparison(false);
        return;
      }

      // Get the latest scan (first one, as they're ordered by created_at desc)
      const latestScan = scansData.scans[0];
      
      // Store the scan ID immediately
      setLatestScanId(latestScan.id);

      // Get the scan details with results
      const scanDetailResponse = await apiFetch(`${API_BASE_URL}/scans/${latestScan.id}`, { method: 'GET' });

      if (!scanDetailResponse.ok) {
        const errorData = await scanDetailResponse.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const scanDetail = await scanDetailResponse.json();
      if (!Array.isArray(scanDetail.results)) {
        throw new Error('Latest scan results are unavailable for comparison.');
      }

      // Convert scan results to the format expected by the compare endpoint.
      // The comparison service considers the first ten ranked rows eligible
      // opportunities, so retain the same scope for the empty-state message.
      const rankedStocks = scanDetail.results.map(result => ({
        ticker: result.ticker,
        price: result.price,
        enterprise_value: 0, // Not available in scan results
        pe_ratio: null,
        pb_ratio: null,
        ps_ratio: null,
        ev_ebitda: null,
        ev_gp: null,
        pe_percentile: null,
        pb_percentile: null,
        ps_percentile: null,
        ev_ebitda_percentile: null,
        ev_gp_percentile: null,
        rv_score: result.rv_score,
        investment_score: result.investment_score,
        entry_timing: result.entry_timing,
        rank: result.rank,
        ten_year_return: result.ten_year_return,
        max_drawdown: result.max_drawdown,
        risk_level: result.risk_level,
        quality_label: result.quality_label,
        sector: result.sector,
        industry: result.industry
      }));

      // Call the compare endpoint
      const compareResponse = await apiFetch(`${API_BASE_URL}/portfolio/${portfolioId}/compare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(rankedStocks),
      });

      if (!compareResponse.ok) {
        const errorData = await compareResponse.json();
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail)
        );
      }

      const comparisonResult = await compareResponse.json();
      const eligibleScanTickers = Array.from(new Set(
        rankedStocks
          .slice(0, 10)
          .map((stock) => String(stock.ticker || '').trim().toUpperCase())
          .filter(Boolean)
      ));
      const heldTickerSet = new Set(
        (portfolioDetail?.holdings || [])
          .filter((holding) => holding && holding.is_active !== false)
          .map((holding) => String(holding.ticker || '').trim().toUpperCase())
          .filter(Boolean)
      );
      const heldEligibleScanCount = eligibleScanTickers.filter(
        (ticker) => heldTickerSet.has(ticker)
      ).length;
      setComparisonData({
        ...comparisonResult,
        latestScanDate: latestScan.created_at,
        scanResultCount: rankedStocks.length,
        eligibleScanCount: eligibleScanTickers.length,
        heldEligibleScanCount,
      });
      
      // Refresh notifications after successful comparison to show newly created alerts
      await fetchNotifications();
    } catch (err) {
      setError(err.message);
      setLatestScanId(null);
    } finally {
      setLoadingComparison(false);
    }
  };

  return (
    <div className="portfolios-page-skin authenticated-page">
      {error && <p className="theme-error">{error}</p>}
      
      {/* Edit Holding Modal */}
      {showEditHoldingModal && (
        <div className="portfolio-modal-backdrop" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="portfolio-modal-panel" style={{
            padding: '30px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Edit {editingHolding?.ticker} Holding</h3>
            <p className="theme-text-muted" style={{ marginBottom: '20px' }}>
              Update the quantity and average buy price for this holding:
            </p>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Quantity: <span className="theme-error">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={editHoldingData.quantity}
                onChange={(e) => setEditHoldingData({
                  ...editHoldingData,
                  quantity: e.target.value
                })}
                placeholder="e.g., 100"
                className="portfolio-modal-input"
                style={{ 
                  width: '100%', 
                  padding: '8px',
                  borderRadius: '4px'
                }}
                autoFocus
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Average Buy Price: <span className="theme-error">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={editHoldingData.average_buy_price}
                onChange={(e) => setEditHoldingData({
                  ...editHoldingData,
                  average_buy_price: e.target.value
                })}
                placeholder="e.g., 150.50"
                className="portfolio-modal-input"
                style={{ 
                  width: '100%', 
                  padding: '8px',
                  borderRadius: '4px'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancelEditHolding}
                className="portfolio-btn portfolio-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmEditHolding}
                className="portfolio-btn portfolio-btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Opportunity Modal */}
      {showAddOpportunityModal && (
        <div className="portfolio-modal-backdrop" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="portfolio-modal-panel" style={{
            padding: '30px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Add {opportunityToAdd} to Portfolio</h3>
            {portfolios.length === 0 ? (
              <>
                <p className="theme-text-muted" style={{ marginBottom: '20px' }}>
                  You don’t have a portfolio yet. Create one on the{' '}
                  <Link to="/portfolios" className="theme-link">Portfolios</Link>
                  {' '}page, then come back here to add this stock.
                </p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleCancelAddOpportunity}
                    className="portfolio-btn portfolio-btn-secondary"
                  >
                    Ok
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="theme-text-muted" style={{ marginBottom: '20px' }}>
                  Enter the quantity and average buy price for this holding:
                </p>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Quantity: <span className="theme-error">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={opportunityHoldingData.quantity}
                    onChange={(e) => setOpportunityHoldingData({
                      ...opportunityHoldingData,
                      quantity: e.target.value
                    })}
                    placeholder="e.g., 100"
                    className="portfolio-modal-input"
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px'
                    }}
                    autoFocus
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Average Buy Price: <span className="theme-error">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={opportunityHoldingData.average_buy_price}
                    onChange={(e) => setOpportunityHoldingData({
                      ...opportunityHoldingData,
                      average_buy_price: e.target.value
                    })}
                    placeholder="e.g., 150.50"
                    className="portfolio-modal-input"
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleCancelAddOpportunity}
                    className="portfolio-btn portfolio-btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmAddOpportunity}
                    className="portfolio-btn portfolio-btn-primary"
                  >
                    Add to Portfolio
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      <div style={{ marginBottom: '20px' }}>
        <h3 className="portfolio-section-heading">Create New Portfolio</h3>
        {!isLoadingEffectiveUserInfo && effectiveUserInfo && effectiveUserInfo.plan_type === 'free' && portfolios.length >= 1 ? (
          <div className="portfolio-notice portfolio-notice-info" style={{
            padding: '15px',
            borderRadius: '5px'
          }}>
            <p style={{ margin: 0 }}>
              Free plan allows only 1 portfolio. Upgrade to Pro or Premium to create more portfolios.
            </p>
          </div>
        ) : (
          <form onSubmit={handleCreatePortfolio} className="portfolio-manager-row">
            <input
              type="text"
              placeholder="Portfolio name"
              value={newPortfolioName}
              onChange={(e) => setNewPortfolioName(e.target.value)}
              required
              className="portfolio-manager-name-input"
            />
            <button type="submit" className="portfolio-btn portfolio-btn-primary portfolio-manager-action-button">
              <span className="portfolio-manager-action-label">Create Portfolio</span>
            </button>
          </form>
        )}
      </div>

      {loading && <p>Loading portfolios...</p>}
      
      {!loading && portfolios.length === 0 && <p>No portfolios yet. Create one to get started!</p>}
      
      {!loading && portfolios.length > 0 && (
        <div className="portfolio-list-section">
          <h3 className="portfolio-section-heading">Your Portfolios</h3>
          <div className="portfolio-manager-list">
            {portfolios.map((portfolio) => (
              <div key={portfolio.id} className="portfolio-manager-row portfolio-manager-list-row">
                <input
                  type="text"
                  value={portfolio.name}
                  readOnly
                  aria-label={`View ${portfolio.name}`}
                  onClick={() => handleViewPortfolio(portfolio.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleViewPortfolio(portfolio.id);
                    }
                  }}
                  className="portfolio-manager-name-input"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePortfolio(portfolio.id);
                  }}
                  className="portfolio-btn portfolio-btn-gold portfolio-manager-action-button"
                >
                  <span className="portfolio-manager-action-label">Delete</span>
                  <span className="portfolio-manager-action-size-reference" aria-hidden="true">Create Portfolio</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {portfolioDetail && (
        <div className="portfolio-panel" style={{ marginTop: '20px', padding: '15px', borderRadius: '5px' }}>
          <h3 className="portfolio-section-heading">{portfolioDetail.name}</h3>
          
          {portfolioDetail.total_value !== null && (
            <div className="portfolio-summary-card portfolio-summary-card-success" style={{ marginBottom: '15px', padding: '10px', borderRadius: '5px' }}>
              <p style={{ margin: '5px 0' }}>
                <strong>Total Value:</strong> ${portfolioDetail.total_value.toFixed(2)}
              </p>
              {portfolioDetail.total_cost !== null && (
                <>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Total Cost:</strong> ${portfolioDetail.total_cost.toFixed(2)}
                  </p>
                  {portfolioDetail.portfolio_gain_loss !== null && (
                    <p className={portfolioDetail.portfolio_gain_loss >= 0 ? 'theme-success' : 'theme-error'} style={{
                      margin: '5px 0',
                      fontWeight: 'bold'
                    }}>
                      <strong>Gain/Loss:</strong> ${portfolioDetail.portfolio_gain_loss.toFixed(2)} 
                      {portfolioDetail.portfolio_gain_loss_percent !== null && 
                        ` (${portfolioDetail.portfolio_gain_loss_percent.toFixed(2)}%)`
                      }
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <button
              onClick={() => setShowAddHolding(!showAddHolding)}
              className="portfolio-btn portfolio-btn-primary"
            >
              {showAddHolding ? 'Cancel' : 'Add Holding'}
            </button>
          </div>

          {showAddHolding && (
            <form onSubmit={handleAddHolding} className="portfolio-add-holding-form">
              <h4 className="portfolio-add-holding-title">Add New Holding</h4>
              <div className="portfolio-form-row">
                <label className="portfolio-form-label">Ticker Symbol: </label>
                <input
                  type="text"
                  value={newHolding.ticker}
                  onChange={(e) => setNewHolding({ ...newHolding, ticker: e.target.value })}
                  required
                  className="portfolio-form-input"
                />
              </div>
              <div className="portfolio-form-row">
                <label className="portfolio-form-label">Quantity (optional): </label>
                <input
                  type="number"
                  step="0.01"
                  value={newHolding.quantity}
                  onChange={(e) => setNewHolding({ ...newHolding, quantity: e.target.value })}
                  className="portfolio-form-input"
                />
              </div>
              <div className="portfolio-form-row">
                <label className="portfolio-form-label">Average Buy Price (optional): </label>
                <input
                  type="number"
                  step="0.01"
                  value={newHolding.average_buy_price}
                  onChange={(e) => setNewHolding({ ...newHolding, average_buy_price: e.target.value })}
                  className="portfolio-form-input"
                />
              </div>
              <div className="portfolio-form-row">
                <label className="portfolio-form-label">Purchase Date (optional): </label>
                <input
                  type="date"
                  value={newHolding.purchase_date}
                  onChange={(e) => setNewHolding({ ...newHolding, purchase_date: e.target.value })}
                  className="portfolio-form-input"
                  max={new Date().toISOString().split('T')[0]} // Prevent future dates
                />
              </div>
              <button type="submit" className="portfolio-btn portfolio-btn-primary">Add to Portfolio</button>
            </form>
          )}

          {/* Rebalance Buttons - Premium Only */}
          {portfolioDetail.holdings && portfolioDetail.holdings.length > 0 && (
            <>
              {!isLoadingEffectiveUserInfo && effectiveUserInfo && effectiveUserInfo.plan_type === 'unlimited' ? (
                <div className="portfolio-rebalance-panel" style={{ marginBottom: '20px', padding: '15px', borderRadius: '5px' }}>
                  <h4 className="theme-section-title" style={{ marginTop: 0, marginBottom: '10px' }}>
                    One-Click Portfolio Rebalancing
                  </h4>
                  <p className="theme-text-muted" style={{ marginBottom: '10px' }}>
                    Recalculate holding quantities using current portfolio value. Average buy prices remain unchanged.
                  </p>
                  <div className="portfolio-panel" style={{ marginBottom: '15px', padding: '10px', borderRadius: '4px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={allowFractionalShares}
                        onChange={(e) => setAllowFractionalShares(e.target.checked)}
                        style={{
                          marginRight: '8px',
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer'
                        }}
                      />
                      <span style={{ fontWeight: '500' }}>Allow fractional shares</span>
                    </label>
                    <p className="theme-text-muted" style={{ margin: '5px 0 0 24px' }}>
                      Turn this off if your broker only supports whole shares.
                    </p>
                  </div>
                  {rebalanceSuccess && (
                    <div className="portfolio-notice portfolio-notice-success theme-success" style={{
                      padding: '10px',
                      marginBottom: '15px',
                      borderRadius: '4px'
                    }}>
                      ✓ {rebalanceSuccess}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleRebalancePortfolio('balanced')}
                      disabled={rebalancing}
                      className="portfolio-btn portfolio-btn-primary"
                    >
                      {rebalancing ? 'Rebalancing...' : 'Balanced'}
                    </button>
                    <button
                      onClick={() => handleRebalancePortfolio('growth')}
                      disabled={rebalancing}
                      className="portfolio-btn portfolio-btn-primary"
                    >
                      {rebalancing ? 'Rebalancing...' : 'Growth'}
                    </button>
                    <button
                      onClick={() => handleRebalancePortfolio('conservative')}
                      disabled={rebalancing}
                      className="portfolio-btn portfolio-btn-primary"
                    >
                      {rebalancing ? 'Rebalancing...' : 'Conservative'}
                    </button>
                  </div>
                </div>
              ) : !isLoadingEffectiveUserInfo && effectiveUserInfo && (
                <div className="portfolio-notice portfolio-notice-info" style={{
                  marginBottom: '20px',
                  padding: '15px',
                  borderRadius: '5px',
                  textAlign: 'center'
                }}>
                  <p className="theme-text" style={{ margin: 0 }}>
                    <strong>One-Click Portfolio Rebalancing</strong> is available on Premium plans.
                  </p>
                </div>
              )}
            </>
          )}

          {portfolioDetail.holdings && portfolioDetail.holdings.length > 0 && (
            <div>
              <h4 className="portfolio-section-heading">Holdings:</h4>
              <div className="portfolio-table-scroll">
              <table className="theme-table portfolio-table" style={{ width: '100%', marginTop: '10px' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Ticker</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Company Name</th>
                    <th className="portfolio-table-score-header" style={{ padding: '8px' }}>Investment Score</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Quantity</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Avg Buy Price</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Current Price</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Current Value</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Gain/Loss</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolioDetail.holdings.map((holding, index) => (
                    <tr key={index}>
                      <td style={{ padding: '8px' }}>
                        <Link 
                          to={`/stock/${holding.ticker}`}
                          title="View stock research page"
                          className="portfolio-ticker-link"
                          onClick={(event) => rememberExpandedPortfolio(selectedPortfolio, event)}
                          style={{ fontWeight: 'bold' }}
                        >
                          {holding.ticker}
                        </Link>
                      </td>
                      <td style={{ padding: '8px' }}>{holding.company_name || 'N/A'}</td>
                      <td className="portfolio-table-score-cell" style={{ padding: '8px' }}>
                        {holding.investment_score !== null && holding.investment_score !== undefined 
                          ? holding.investment_score.toFixed(1) 
                          : 'N/A'
                        }
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {holding.quantity !== null ? holding.quantity.toFixed(2) : 'N/A'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {holding.average_buy_price !== null ? `$${holding.average_buy_price.toFixed(2)}` : 'N/A'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {holding.current_price !== null ? `$${holding.current_price.toFixed(2)}` : 'N/A'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {holding.current_value !== null ? `$${holding.current_value.toFixed(2)}` : 'N/A'}
                      </td>
                      <td className={holding.gain_loss !== null ? (holding.gain_loss >= 0 ? 'theme-success' : 'theme-error') : ''} style={{
                        padding: '8px', 
                        textAlign: 'center',
                        fontWeight: holding.gain_loss !== null ? 'bold' : 'normal'
                      }}>
                        {holding.gain_loss !== null 
                          ? `$${holding.gain_loss.toFixed(2)} (${holding.gain_loss_percent.toFixed(2)}%)`
                          : 'N/A'
                        }
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <div className="portfolio-holding-actions">
                          <button
                            onClick={() => handleEditHolding(holding)}
                            className="portfolio-btn portfolio-btn-secondary portfolio-btn-compact"
                            style={{ marginRight: '5px' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleRemoveHolding(holding.ticker)}
                            className="portfolio-btn portfolio-btn-gold portfolio-btn-compact"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
          
          {portfolioDetail.holdings && portfolioDetail.holdings.length === 0 && (
            <p>No holdings in this portfolio yet. Add some stocks to get started!</p>
          )}

          {isPremium && (
            <section className="portfolio-panel" style={{ marginTop: '30px', borderTop: '2px solid #ddd', paddingTop: '20px' }} aria-label="Premium portfolio alert settings">
              <h3 className="portfolio-section-heading">Premium Strategy Lab Alert Settings</h3>
              <p className="theme-text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>Alerts are issued monthly because the fundamentals behind the rankings are updated monthly. After each update, you’ll be alerted if a stock leaves your filtered Top‑N and a higher-scoring stock enters by more than your tolerance.</p>
              <p className="theme-text-muted">Choose this portfolio’s published Strategy Lab Top-N. Alerts compare monthly Top-N replacements; they do not change Strategy Lab rank or selection.</p>
              {premiumAlertSettingsError && <p className="theme-error">{premiumAlertSettingsError}</p>}
              {premiumStrategyFilterOptionsError && <p className="theme-error">{premiumStrategyFilterOptionsError}</p>}
              {premiumAlertSettings && (
                <>
                  <div className="portfolio-form-row">
                    <label className="portfolio-form-label">Country
                      <select aria-label="Strategy Lab country" value={premiumAlertSettings.country || ''} onChange={(e) => {
                        const next = premiumStrategyHierarchy.clearDependentFilters({
                          universe: premiumAlertSettings.universe_code,
                          country: e.target.value,
                          sector: premiumAlertSettings.sector,
                          industry: premiumAlertSettings.industry,
                        });
                        setPremiumAlertSettings({ ...premiumAlertSettings, country: next.country, sector: next.sector, industry: next.industry });
                      }} className="portfolio-form-input">
                        <option value="">All Countries</option>
                        {premiumCountryOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </label>
                    <label className="portfolio-form-label">Universe
                      <select aria-label="Strategy Lab universe" value={premiumAlertSettings.universe_code || 'ALL'} onChange={(e) => { const universe_code = e.target.value; setPremiumAlertSettings({ ...premiumAlertSettings, universe_code, country: null, sector: null, industry: null }); fetchPremiumStrategyFilterOptions(universe_code); }} className="portfolio-form-input">
                        <option value="ALL">All supported stocks</option><option value="SP500">S&amp;P 500</option><option value="NASDAQ100">Nasdaq-100</option><option value="DOW30">Dow 30</option><option value="TSX60">S&amp;P/TSX 60</option><option value="TSX_COMPOSITE">S&amp;P/TSX Composite</option>
                      </select>
                    </label>
                    <label className="portfolio-form-label">Sector
                      <select aria-label="Strategy Lab sector" value={premiumAlertSettings.sector || ''} onChange={(e) => {
                        const next = premiumStrategyHierarchy.clearDependentFilters({
                          universe: premiumAlertSettings.universe_code,
                          country: premiumAlertSettings.country,
                          sector: e.target.value,
                          industry: premiumAlertSettings.industry,
                        });
                        setPremiumAlertSettings({ ...premiumAlertSettings, sector: next.sector, industry: next.industry });
                      }} className="portfolio-form-input">
                        <option value="">All Sectors</option>
                        {premiumSectorOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </label>
                    <label className="portfolio-form-label">Industry
                      <select aria-label="Strategy Lab industry" value={premiumAlertSettings.industry || ''} onChange={(e) => setPremiumAlertSettings({ ...premiumAlertSettings, industry: e.target.value || null })} className="portfolio-form-input">
                        <option value="">All Industries</option>
                        {premiumIndustryOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="portfolio-form-row">
                    <label className="portfolio-form-label">Top-N rank cutoff <input aria-label="Top-N rank cutoff" type="number" min="1" max="500" value={premiumAlertSettings.top_n} onChange={(e) => setPremiumAlertSettings({ ...premiumAlertSettings, top_n: Number(e.target.value) })} className="portfolio-form-input" /></label>
                    <label className="portfolio-form-label">Investor Score tolerance <input aria-label="Investor Score tolerance" type="number" min="0" max="100" step="0.1" value={premiumAlertSettings.investor_score_tolerance} onChange={(e) => setPremiumAlertSettings({ ...premiumAlertSettings, investor_score_tolerance: Number(e.target.value) })} className="portfolio-form-input" /></label>
                  </div>
                  <p className="theme-text-muted">A paired entry and exit alert is sent only when the entering ticker’s Investor Score exceeds the departing ticker’s by more than this tolerance. Default: 2.0 points.</p>
                  <button type="button" className="portfolio-btn portfolio-btn-primary" onClick={savePremiumAlertSettings} disabled={savingPremiumAlertSettings}>{savingPremiumAlertSettings ? 'Saving…' : 'Save Alert Settings'}</button>
                  <div aria-live="polite" role="status" className="theme-text-muted" style={{ marginTop: '10px' }}>
                    {savingPremiumAlertSettings && 'Saving…'}
                    {!savingPremiumAlertSettings && premiumAlertSettingsDirty && 'Unsaved changes'}
                    {!savingPremiumAlertSettings && !premiumAlertSettingsDirty && savedPremiumAlertSettings && (
                      <div aria-label="Saved Premium alert settings">
                        <strong>Settings saved for {selectedPortfolioName}</strong>
                        <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
                          <li>Country: {savedSettingValue(savedPremiumAlertSettings.country, 'All Countries')}</li>
                          <li>Universe: {getRankingUniverseLabel(savedPremiumAlertSettings.universe_code)}</li>
                          <li>Sector: {savedSettingValue(savedPremiumAlertSettings.sector, 'All Sectors')}</li>
                          <li>Industry: {savedSettingValue(savedPremiumAlertSettings.industry, 'All Industries')}</li>
                          <li>Top-N: {savedPremiumAlertSettings.top_n}</li>
                          <li>Investor Score tolerance: {savedPremiumAlertSettings.investor_score_tolerance}</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
            </section>
          )}

      <div style={{ marginTop: '10px', marginBottom: '20px' }}>
            <button
              onClick={() => handleCompareWithLatestScan(selectedPortfolio)}
              disabled={loadingComparison}
              className="portfolio-btn portfolio-btn-secondary"
              style={{ marginRight: '10px' }}
            >
              {loadingComparison ? 'Loading Comparison...' : 'Compare With Latest Scan'}
            </button>
            {latestScanId && (
              <button
                onClick={() => handleExportPDF(latestScanId, selectedPortfolio)}
                className="portfolio-btn portfolio-btn-secondary"
              >
                Export Report with Portfolio Analysis
              </button>
            )}
          </div>

          {comparisonData && (
            <div className="portfolio-panel" style={{ marginTop: '20px', padding: '15px', borderRadius: '5px' }}>
              <h3 className="portfolio-section-heading theme-section-title" style={{ marginTop: 0, marginBottom: '5px' }}>Portfolio Comparison Analysis</h3>
              <p className="theme-text-muted" style={{ marginBottom: '15px' }}>
                Compared with scan from {new Date(comparisonData.latestScanDate).toLocaleString()}
              </p>

              {/* Strong Holds Section */}
              {comparisonData.matches && comparisonData.matches.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 className="portfolio-table-heading portfolio-table-heading-success" style={{ marginBottom: '10px' }}>
                    Strong Holds ({comparisonData.matches_count})
                  </h4>
                  <p className="theme-text-muted" style={{ marginBottom: '10px' }}>
                    Stocks you own that are still ranked highly in the latest scan
                  </p>
                  <PortfolioOpportunityTable
                    stocks={comparisonData.matches}
                    onTickerNavigate={(event) => rememberExpandedPortfolio(selectedPortfolio, event)}
                    owned
                  />
                </div>
              )}

              {/* New Opportunities Section */}
              {comparisonData.opportunities && comparisonData.opportunities.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 className="portfolio-table-heading portfolio-table-heading-success" style={{ marginBottom: '10px' }}>
                    New Opportunities ({comparisonData.opportunities_count})
                  </h4>
                  <p className="theme-text-muted" style={{ marginBottom: '10px' }}>
                    Highly ranked stocks from the latest scan that are not currently held in this portfolio
                  </p>
                  <PortfolioOpportunityTable
                    stocks={comparisonData.opportunities}
                    onAddOpportunityToPortfolio={handleAddOpportunityToPortfolio}
                    onTickerNavigate={(event) => rememberExpandedPortfolio(selectedPortfolio, event)}
                  />
                </div>
              )}


              {comparisonData.matches_count === 0 && comparisonData.opportunities_count === 0 && (
                <p className="theme-text-muted" style={{ fontStyle: 'italic' }}>
                  {comparisonData.scanResultCount === 0
                    ? 'The latest scan has no results to compare.'
                    : comparisonData.eligibleScanCount > 0
                      && comparisonData.heldEligibleScanCount === comparisonData.eligibleScanCount
                      ? 'No new opportunities in this scan. Your portfolio already holds these stocks.'
                      : 'No comparison data available. Your portfolio may not overlap with the latest scan results.'}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Portfolios;
